
const cartMutationTracker = (() => {
  const mutationPath = /\/cart\/(add|change|update|clear)(\.js)?$/;
  let pending = 0;
  let version = 0;
  let waiters = [];

  const isMutation = (url) => {
    try {
      return mutationPath.test(new URL(url, window.location.href).pathname);
    } catch (error) {
      return false;
    }
  };
  const start = () => {
    pending += 1;
    version += 1;
  };
  const end = () => {
    pending = Math.max(pending - 1, 0);
    version += 1;
    if (pending === 0) {
      waiters.forEach((resolve) => resolve());
      waiters = [];
    }
  };

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || String(input);
    if (!isMutation(url)) return nativeFetch.apply(this, arguments);
    start();
    try {
      const request = nativeFetch.apply(this, arguments);
      request.then(end, end);
      return request;
    } catch (error) {
      end();
      throw error;
    }
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.isCartMutation = isMutation(url);
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (!this.isCartMutation) return nativeSend.apply(this, arguments);
    start();
    this.addEventListener('loadend', end, { once: true });
    try {
      return nativeSend.apply(this, arguments);
    } catch (error) {
      this.removeEventListener('loadend', end);
      end();
      throw error;
    }
  };

  return {
    get version() {
      return version;
    },
    // Resolves once no writes are in flight, or after maxWait so a hung request can't block the refresh forever
    settled(maxWait = 10000) {
      if (pending === 0) return Promise.resolve();
      return new Promise((resolve) => {
        waiters.push(resolve);
        setTimeout(resolve, maxWait);
      });
    }
  };
})();

function isBackForwardLoad() {
  const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (navigation) return navigation.type === 'back_forward';
  return performance.navigation && performance.navigation.type === 2;
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.refreshRequestId = 0;
    if (isBackForwardLoad()) this.refreshFromServer();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refreshFromServer();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    document.dispatchEvent(new CustomEvent("cart:open"));

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => { this.classList.add('animate', 'active') });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.querySelector("html").classList.add("disable-scroll");
    sessionStorage.setItem("vase_added", "");

    if (typeof window.applyVdayAddonDrawerState === 'function') {
      window.applyVdayAddonDrawerState(window.hasCartVday);
    }

    if (this.refreshFailed) this.refreshFromServer();
  }

  close() {
    this.classList.remove('active');
    if (!this.activeElement.classList.contains("product-form__submit")) {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    document.querySelector("html").classList.remove("disable-scroll");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      //this.open();
    });
  }


  async refreshFromServer(attempt = 0) {
    const requestId = ++this.refreshRequestId;
    clearTimeout(this.refreshRetryTimer);

    if (!window.checkoutDisabled) {
      this.querySelectorAll('#CartDrawer-Checkout').forEach((button) => button.removeAttribute('disabled'));
    }

    await cartMutationTracker.settled();
    if (requestId !== this.refreshRequestId) return;

    const startVersion = cartMutationTracker.version;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    this.setRefreshing(true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`, {
        cache: 'no-store',
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      const sections = await response.json();
      if (!sections || !sections['cart-drawer'] || !sections['cart-icon-bubble']) {
        throw new Error('Cart refresh returned no sections');
      }
      if (requestId !== this.refreshRequestId) return;

      if (cartMutationTracker.version !== startVersion) {
        this.setRefreshing(false);
        this.refreshFromServer();
        return;
      }

      this.renderRefreshedContents(sections);
      this.refreshFailed = false;
    } catch (error) {
      if (requestId !== this.refreshRequestId) return;
      console.error(error);

      const retryDelay = [1000, 3000, 5000][attempt];
      if (retryDelay === undefined) {
        this.refreshFailed = true;
      } else {
        this.refreshRetryTimer = setTimeout(() => this.refreshFromServer(attempt + 1), retryDelay);
      }
    } finally {
      clearTimeout(timeout);
      if (requestId === this.refreshRequestId) this.setRefreshing(false);
    }
  }

  renderRefreshedContents(sections) {
    const getScrollContainer = () =>
      this.querySelector('.drawer-main-wrap .simplebar-content-wrapper') || this.querySelector('.drawer-main-wrap');
    const scrollTop = getScrollContainer()?.scrollTop || 0;
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;

    this.renderContents({ sections });

    const isEmpty = !!this.querySelector('.drawer__inner-empty');
    this.classList.toggle('is-empty', isEmpty);

    requestAnimationFrame(() => {
      const scrollContainer = getScrollContainer();
      if (scrollContainer) scrollContainer.scrollTop = scrollTop;
    });

    if (focusedId !== null && this.classList.contains('active')) {
      const container = isEmpty ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = (focusedId && document.getElementById(focusedId)) || this.querySelector('.drawer__inner');
      trapFocus(container, focusElement);
    }

    ['hide_addon_tab', 'refreshFsLightbox', 'initCountdown'].forEach((helper) => {
      try {
        if (typeof window[helper] === 'function') window[helper]();
      } catch (error) {
        console.error(error);
      }
    });

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: isEmpty ? { itemCount: 0 } : {} }));
    document.dispatchEvent(new CustomEvent('cart:refresh-addons'));
  }

  setRefreshing(isRefreshing) {
    this.classList.toggle('cart-drawer--refreshing', isRefreshing);
    this.toggleAttribute('aria-busy', isRefreshing);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
